// Every piece of lookalike-explainer logic, in one pure module: the builder
// (`src/screens/audience.tsx`) and the planner only render what these functions decide, and
// `pnpm test` reaches them here without a DOM.

import type {
  AudienceBucketValue,
  InsightStatus,
  LookalikeCaveat,
  LookalikeSuppressedDimension,
  LookalikeSuppressionReason,
  Rule,
  RuleGroup,
} from '@/api/types.gen';

// ── Why this module declares its own row shapes ─────────────────────────────
//
// `types.gen.ts` renders Rust `i64` as `bigint`, but the API layer maps every
// `bigint` to `number` on the way in (`JsonNumbers<T>` in `api/manage-hooks.ts`), so
// `AudiencePreview` — what a component actually holds — carries `number` in the same
// fields. Typing against either one alone makes the module unusable from the other
// half of the app, and a cast at each call site is how a real mismatch gets hidden.
//
// So the numeric fields are `number | bigint` and every read goes through `Number()`.
// Structural, so both generated shapes satisfy it without conversion.

type Scalar = number | bigint;

export interface BucketWeightLike {
  value: AudienceBucketValue | null;
  display_label: string;
  order: number;
  weight: Scalar;
  seed_percentage: number;
  baseline_percentage: number;
}

export interface DimensionExplanationLike {
  dimension: string;
  status: InsightStatus;
  buckets: BucketWeightLike[];
  total_contribution: Scalar;
}

export interface CurvePointLike {
  share_bps: number;
  people: Scalar;
}

export interface ModelExplanationLike {
  status: InsightStatus;
  cohort_resolved: Scalar;
  lambda_permille: Scalar;
  dimensions: DimensionExplanationLike[];
  suppressed_dimensions: LookalikeSuppressedDimension[];
  size_curve: CurvePointLike[];
  selection: { share_bps: number; people: Scalar; approximate: boolean };
  caveats: LookalikeCaveat[];
  computed_at: string;
  /// When an accelerated fit is queued but not yet published — the signal that a non-ready
  /// status means "minutes away" rather than "waits for tonight". Optional because cached
  /// responses predating the field omit it.
  fit_requested_at?: string | null;
}

/// A preview breakdown, whichever numeric rendering it carries.
type BreakdownLike = { kind: string } & Record<string, unknown>;

/// The share every lookalike leaf is pinned to when building a *preview* request.
///
/// Any constant works; what matters is that it is a constant. See
/// [`canonicalizeLookalikeShare`].
export const PREVIEW_PINNED_SHARE_BPS = 5;

/// Pin every lookalike leaf's share, at any depth, so a preview request body does not change
/// when the slider moves.
///
/// The server returns a *whole* size curve — one point per selectable stop — computed from the
/// published histogram. So the slider needs no server round trip: it reads
/// [`curvePointAt`] out of the response it already has. But the request body is hashed into the
/// TanStack query key, so leaving the real share in it would mint a new key per slider position
/// and refetch on every drag: six requests to learn six numbers the first response already
/// carried.
///
/// **This is for the preview request only.** The tree that gets *saved* must carry the
/// operator's actual choice — pinning it there would silently save the wrong audience size.
/// Nothing enforces that but the call sites, which is why the two are named differently.
export function canonicalizeLookalikeShare(
  rules: RuleGroup,
  pinnedShareBps: number = PREVIEW_PINNED_SHARE_BPS,
): RuleGroup {
  const pinRule = (rule: Rule): Rule => {
    if (rule.kind === 'lookalike') {
      return { ...rule, top_share_bps: pinnedShareBps };
    }
    if (rule.kind === 'group') {
      return { ...rule, ...pinGroup(rule) };
    }
    return rule;
  };
  const pinGroup = (group: RuleGroup): RuleGroup => ({
    ...group,
    rules: group.rules.map(pinRule),
  });
  return pinGroup(rules);
}

/// The share the first lookalike leaf names, or `null` when the tree has none.
///
/// `null` is the discriminator the builder branches on: a tree with no lookalike leaf is a
/// customer audience and goes to the count-only endpoint, while a prospecting one needs the
/// whole model projection. Reading the tree rather than an `audience_type` column keeps the two
/// from disagreeing — the tree is the definition, the column is a label.
export function firstLookalikeShareBps(rules: RuleGroup | null | undefined): number | null {
  if (!rules) return null;
  for (const rule of rules.rules) {
    if (rule.kind === 'lookalike') return rule.top_share_bps;
    if (rule.kind === 'group') {
      const nested = firstLookalikeShareBps(rule);
      if (nested !== null) return nested;
    }
  }
  return null;
}

/// Set every lookalike leaf's share to the operator's choice. The inverse of the pinning above,
/// for the tree that is saved rather than previewed.
export function withLookalikeShare(rules: RuleGroup, shareBps: number): RuleGroup {
  return canonicalizeLookalikeShare(rules, shareBps);
}

/// The lookalike model explanation carried by a preview response, if it has one.
export function lookalikeModelOf(
  breakdowns: readonly BreakdownLike[] | undefined,
): ModelExplanationLike | null {
  const found = breakdowns?.find((breakdown) => breakdown.kind === 'lookalike_model');
  return (found?.model as ModelExplanationLike | undefined) ?? null;
}

/// The selectable stops, ascending.
export function curveStops(model: ModelExplanationLike): number[] {
  return model.size_curve.map((point) => point.share_bps);
}

/// The curve point for a requested share.
///
/// The slider positions *are* the published stops, so an off-stop value is an invalid caller
/// state rather than something this presentation helper may round. Returning `null` keeps that
/// mismatch visible and prevents the UI from displaying the size of a different audience.
export function curvePointAt(
  model: ModelExplanationLike,
  shareBps: number,
): CurvePointLike | null {
  return model.size_curve.find((point) => point.share_bps === shareBps) ?? null;
}

/// Why a prospect preview cannot be used as an audience size, or `null` when it can.
///
/// Only a READY model with no published point at the selected share blocks now — that is a
/// *different share* problem the operator must act on. A model that has not been fitted yet is
/// deliberately NOT a blocker any more: composing a lookalike is itself what requests the
/// accelerated fit (the preview endpoint stamps it), so "not fitted" is a transient state the
/// preview poll resolves in minutes. Blocking on it was the compose-time deadlock — a gate
/// waiting for a fit that nothing had requested.
export type ProspectPreviewBlocker = 'no-size-at-share';

export function prospectPreviewBlocker(
  modelStatus: InsightStatus | null | undefined,
  point: CurvePointLike | null,
): ProspectPreviewBlocker | null {
  if (modelStatus !== 'ready') return null;
  if (!point) return 'no-size-at-share';
  return null;
}

const PROSPECT_BLOCKER_TEXT: Record<ProspectPreviewBlocker, string> = {
  'no-size-at-share':
    'The published model has no size at this share. Pick another slice, or wait for a fit built from a larger seed.',
};

export function prospectPreviewBlockerMessage(blocker: ProspectPreviewBlocker): string {
  return PROSPECT_BLOCKER_TEXT[blocker];
}

/// Whether the model is still being (or waiting to be) fitted. The PENDING treatment, the
/// polling, and the copy all key off this one predicate so they cannot disagree.
export function lookalikeFitPending(
  modelStatus: InsightStatus | null | undefined,
): boolean {
  return modelStatus != null && modelStatus !== 'ready';
}

/// The heading for a pending fit. The size gauge and the lookalike panel both read it, from the
/// same `fit_requested_at`, so one cannot say "running" while the other says "pending".
export function fitPendingHeading(fitRequestedAt: string | null | undefined): string {
  return fitRequestedAt ? 'Model fitting is running' : 'Model fitting is pending';
}

/// The honest copy for a pending fit. A pending accelerated request means minutes; its
/// absence (archived seed, or a response cached before the field existed) keeps the
/// overnight framing.
export function fitPendingCopy(fitRequestedAt: string | null | undefined): string {
  return fitRequestedAt
    ? 'This lookalike is being fitted now — its estimated size usually appears within a few minutes.'
    : 'This lookalike has not been fitted yet. Its factors and size are published by the overnight run.';
}

/// Poll the preview while a lookalike fit is pending, stop the moment it publishes (or the
/// tree stops being a lookalike). Same shape as `creativePreviewPollInterval`.
export function lookalikeFitPollInterval(
  breakdowns: BreakdownLike[] | undefined,
  intervalMs: number,
): number | false {
  const model = lookalikeModelOf(breakdowns);
  if (!model) return false;
  return lookalikeFitPending(model.status) ? intervalMs : false;
}

/// The preview a builder may treat as complete, or `null` when it may not.
///
/// For a prospect audience this is gated on the model being usable, not merely on a response
/// having arrived. A response for an unfitted model is a successful request carrying no
/// audience — treating its truthiness as completion is what let a campaign be confirmed, and
/// then saved, against a lookalike with no size.
export function completedAudiencePreview<T>(
  isProspecting: boolean,
  blocker: ProspectPreviewBlocker | null,
  estimateData: T | undefined,
  modelPreviewData: T | undefined,
): T | null {
  if (!isProspecting) return estimateData ?? null;
  return blocker ? null : (modelPreviewData ?? null);
}

/// The number to display, which must belong to the share the operator selected.
///
/// There is deliberately no fallback to the preview response's own `count`: that count is
/// computed at the *pinned* share the request was canonicalized to, not the selected one, so
/// showing it here would label one slice's headcount with another's. `size_curve` omits
/// points a small fit cannot reach, so the mismatch is reachable rather than theoretical.
export function resolvedAudiencePreviewCount(
  isProspecting: boolean,
  blocker: ProspectPreviewBlocker | null,
  point: CurvePointLike | null,
  estimateData: { count?: Scalar } | undefined,
): number | null {
  if (isProspecting) {
    if (blocker || !point) return null;
    return Number(point.people);
  }
  const count = estimateData?.count;
  return count === undefined || count === null ? null : Number(count);
}

/// One factor bar: how much of the model's discrimination a dimension supplies.
export interface FactorContribution {
  dimension: string;
  /// Sum of `|weight|` across the dimension. A divergence, **not** a headcount — which is why
  /// this cannot reuse `InsightBreakdown`, whose numeric slot is a user count and whose bar is
  /// `users / total`.
  contribution: number;
  /// `contribution` as a share of the largest dimension's, for the bar width. The largest
  /// rather than the sum, so the strongest factor fills the track and the rest are read
  /// against it — a share-of-sum makes every bar short as soon as there are six dimensions.
  fraction: number;
  /// The single strongest pull, for the label beside the bar.
  strongest: BucketWeightLike | null;
}

/// Dimensions ranked by how much they moved the score, strongest first.
export function factorContributions(
  model: ModelExplanationLike,
): FactorContribution[] {
  const ranked = model.dimensions
    .filter((dimension) => dimension.status === 'ready')
    .map((dimension) => ({
      dimension: dimension.dimension,
      contribution: Number(dimension.total_contribution),
      strongest: strongestBucket(dimension),
    }))
    .sort((left, right) => right.contribution - left.contribution);
  const largest = ranked[0]?.contribution ?? 0;
  return ranked.map((entry) => ({
    ...entry,
    fraction: largest > 0 ? entry.contribution / largest : 0,
  }));
}

/// The bucket with the largest absolute weight — the one worth naming beside a factor bar.
///
/// Ties break toward the *positive* bucket: "who this audience is" reads better than "who it
/// is not", and a tie means the two are equally informative.
export function strongestBucket(
  dimension: DimensionExplanationLike,
): BucketWeightLike | null {
  let best: BucketWeightLike | null = null;
  for (const bucket of dimension.buckets) {
    if (best === null) {
      best = bucket;
      continue;
    }
    const bucketWeight = absWeight(bucket);
    const bestWeight = absWeight(best);
    if (bucketWeight > bestWeight) best = bucket;
    else if (bucketWeight === bestWeight && bucket.weight > best.weight) best = bucket;
  }
  return best;
}

function absWeight(bucket: BucketWeightLike): number {
  const weight = Number(bucket.weight);
  return weight < 0 ? -weight : weight;
}

/// A bucket's weight keyed `dimension|display_label`, for joining the explainer onto the
/// Planner's existing per-dimension comparison rows.
export function weightsByBucketKey(
  model: ModelExplanationLike,
): Map<string, number> {
  const weights = new Map<string, number>();
  for (const dimension of model.dimensions) {
    for (const bucket of dimension.buckets) {
      weights.set(bucketKey(dimension.dimension, bucket.display_label), Number(bucket.weight));
    }
  }
  return weights;
}

export function bucketKey(dimension: string, displayLabel: string): string {
  return `${dimension}|${displayLabel}`;
}

/// The seed cohort's share per bucket for one dimension, as a fourth comparison series.
///
/// Keyed by `display_label` so it aligns with whatever bucket rows the Planner already renders,
/// without assuming the two lists are the same length or the same order.
export function lookalikeSeriesFor(
  model: ModelExplanationLike,
  dimension: string,
): Map<string, number> {
  const series = new Map<string, number>();
  const found = model.dimensions.find((entry) => entry.dimension === dimension);
  if (!found || found.status !== 'ready') return series;
  for (const bucket of found.buckets) {
    series.set(bucket.display_label, bucket.seed_percentage);
  }
  return series;
}

/// `true` iff this dimension was withheld rather than measured.
///
/// The distinction the whole explainer exists to carry: a withheld dimension still has a fitted
/// weight and still moved the score. Rendering it as zero would tell an operator the factor did
/// not matter, which is the opposite of true — so a suppressed row must render as an **absent**
/// track, never an empty one.
export function isSuppressed(model: ModelExplanationLike, dimension: string): boolean {
  return model.suppressed_dimensions.some((entry) => entry.dimension === dimension);
}

export function suppressionReasonOf(
  model: ModelExplanationLike,
  dimension: string,
): LookalikeSuppressionReason | null {
  return (
    model.suppressed_dimensions.find((entry) => entry.dimension === dimension)?.reason ?? null
  );
}

const SUPPRESSION_TEXT: Record<LookalikeSuppressionReason, string> = {
  below_privacy_floor:
    'Withheld: at least one group here held too few people to publish. The factor still moved the model — this is not a zero.',
  not_implemented:
    'Not measured: this dimension has no data source yet, so the model never fitted it.',
};

export function suppressionText(reason: LookalikeSuppressionReason): string {
  return SUPPRESSION_TEXT[reason];
}

const CAVEAT_TEXT: Record<LookalikeCaveat, string> = {
  assumes_independent_dimensions:
    'Factors are combined as though independent, so correlated ones — income and education, for instance — count twice.',
  size_is_modelled:
    'This size is modelled from the published score distribution, not counted. The exact set is built overnight.',
  size_is_before_customer_exclusion:
    'Counts everyone the model selected, including people who are already your customers. The prospects delivered are fewer.',
  weights_shrunk_for_small_cohort:
    'The seed was small, so the factors are pulled toward the average to avoid presenting noise as signal.',
  some_dimensions_withheld:
    'Some factors are withheld — see the greyed rows. They still moved the model.',
};

export function caveatText(caveat: LookalikeCaveat): string {
  return CAVEAT_TEXT[caveat];
}

/// A bucket the operator can turn into a filter, or `null` when it has no rule counterpart.
///
/// The distance bands are `null` on purpose: a band is defined relative to eight derived
/// centres, so no `Rule::Geo` expresses it. Offering a dead "filter on this" control would be
/// worse than offering none.
export function filterableValue(bucket: BucketWeightLike): AudienceBucketValue | null {
  return bucket.value;
}

/// Shrinkage as a percentage, for display. 1000 permille is unshrunk.
export function shrinkagePercent(model: ModelExplanationLike): number {
  return Math.round(Number(model.lambda_permille) / 10);
}
